import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderOpen, 
  Video, 
  TrendingUp, 
  Wand2, 
  Upload,
  Settings,
  Share2,
  Play,
  Plus,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { slideUp, fadeIn } from '@/lib/animations';
import EmptyStateMessage from '../components/EmptyStateMessage';
import DeleteProjectDialog from '../components/DeleteProjectDialog';
import useAuth from '../hooks/useAuth';
import apiService from '../services/api';
import { Project, ProcessingWarning } from '../types';

// Define ProjectStatus enum locally since it's not in the main types file
enum ProjectStatus {
  New = 'New',
  UploadComplete = 'Upload Complete',
  Processing = 'Processing',
  ProcessingComplete = 'Processing Complete',
  UploadedToSocials = 'Uploaded to Socials',
  Failed = 'Failed'
}

const ProjectDashboard: React.FC = () => {
  console.log('[DASHBOARD] 🏠 ProjectDashboard component mounting');
  
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  console.log('[DASHBOARD] 👤 Auth state:', { isAuthenticated, userId: user?.id?.substring(0, 8) });

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    project: Project | null;
    isDeleting: boolean;
  }>({
    isOpen: false,
    project: null,
    isDeleting: false
  });

  console.log('[DASHBOARD] 📊 Component state:', { projectsCount: projects.length, loading, hasError: !!error });

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[PROJECTS] Fetching projects for dashboard');
      const response = await apiService.getProjects();
      
      if (response.data?.status === 'success') {
        setProjects(response.data.data || []);
      } else {
        throw new Error('Failed to fetch projects');
      }
    } catch (error: any) {
      console.error('[PROJECTS] Error fetching projects:', error);
      setError(error.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Empty dependency array - prevents infinite loop

  useEffect(() => {
    if (user?.id && !loading) {
      fetchProjects();
    }
  }, [user?.id, fetchProjects]); // ✅ Fixed dependencies

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleEditProject = (projectId: string) => {
    navigate(`/projects/${projectId}/edit`);
  };

  const handleDeleteProject = (project: Project) => {
    setDeleteDialog({
      isOpen: true,
      project,
      isDeleting: false
    });
  };

  const handleDeleteDialogClose = () => {
    if (!deleteDialog.isDeleting) {
      setDeleteDialog({
        isOpen: false,
        project: null,
        isDeleting: false
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.project) return;

    setDeleteDialog(prev => ({ ...prev, isDeleting: true }));

    try {
      console.log('[PROJECT_DELETE] Deleting project:', deleteDialog.project.id);
      
      const response = await apiService.deleteProject(deleteDialog.project.id);
      
      if (response.data?.status === 'success') {
        console.log('[PROJECT_DELETE] Project deleted successfully');
        
        // Remove the project from the local state
        setProjects(prev => prev.filter(p => p.id !== deleteDialog.project!.id));
        
        // Close the dialog
        setDeleteDialog({
          isOpen: false,
          project: null,
          isDeleting: false
        });
        
        // Show success message (you could add a toast notification here)
        console.log('[PROJECT_DELETE] Project deleted successfully');
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (error: any) {
      console.error('[PROJECT_DELETE] Error deleting project:', error);
      setError(error.message || 'Failed to delete project');
      
      // Reset deleting state but keep dialog open to show error
      setDeleteDialog(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleRetryProject = async (project: Project) => {
    try {
      console.log('[PROJECT_RETRY] Retrying project:', project.id);
      
      const response = await apiService.processProject(project.id);
      
      if (response.data?.status === 'success') {
        console.log('[PROJECT_RETRY] Project retry initiated successfully');
        
        // Update the project status in local state
        setProjects(prev => prev.map(p => 
          p.id === project.id 
            ? { ...p, status: ProjectStatus.Processing, processing_started_at: new Date().toISOString() }
            : p
        ));
      } else {
        throw new Error('Failed to retry project processing');
      }
    } catch (error: any) {
      console.error('[PROJECT_RETRY] Error retrying project:', error);
      setError(error.message || 'Failed to retry project processing');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'text-gray-500 bg-gray-100';
      case 'Upload Complete':
        return 'text-blue-600 bg-blue-100';
      case 'Processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'Processing Complete':
        return 'text-green-600 bg-green-100';
      case 'Uploaded to Socials':
        return 'text-purple-600 bg-purple-100';
      case 'Failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-500 bg-gray-100';
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'New':
        return 'New';
      case 'Upload Complete':
        return 'Ready';
      case 'Processing':
        return 'Processing';
      case 'Processing Complete':
        return 'Compiled';
      case 'Uploaded to Socials':
        return 'Published';
      case 'Failed':
        return 'Failed';
      default:
        return status;
    }
  };

  const hasFailedProjects = projects.some(p => p.status === 'Failed');

  const stats = {
    totalProjects: projects.length,
    recentProjects: projects.filter(p => 
      new Date(p.created_at).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
    ).length,
    publishedProjects: projects.filter(p => p.status === 'Uploaded to Socials').length,
    failedProjects: projects.filter(p => p.status === 'Failed').length,
    totalVideos: projects.reduce((sum, p) => sum + (p.compilation_metadata?.videoCount || 0), 0),
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <TrendingUp className="h-12 w-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load Projects</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchProjects}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Background elements */}
      <div className="absolute inset-0 -z-10 opacity-30">
        <div className="absolute right-0 top-1/4 w-1/3 h-1/3 bg-primary/5 rounded-full filter blur-3xl" />
        <div className="absolute left-0 bottom-1/4 w-1/3 h-1/3 bg-accent/5 rounded-full filter blur-3xl" />
      </div>

      <div className="container px-4 md:px-6 py-8">
        {/* Welcome Section */}
        <div className={cn(slideUp(), "mb-8")}>
          <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-primary/10 text-primary mb-4">
            Project Dashboard
          </span>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl mb-2">
            Welcome{user?.name ? `, ${user.name}` : ''}
          </h1>
          <p className="text-lg text-muted-foreground">
            View your organisations projects and transform your footage into story-led content
          </p>
        </div>

        {/* Failed Projects Alert */}
        {hasFailedProjects && (
          <div className={cn(fadeIn(100), "mb-6")}>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 mb-1">
                    Projects Need Attention
                  </h3>
                  <p className="text-sm text-red-700">
                    {projects.filter(p => p.status === 'Failed').length} project{projects.filter(p => p.status === 'Failed').length > 1 ? 's' : ''} failed during processing. 
                    You can retry processing or check the error details below.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className={cn(fadeIn(200), "grid grid-cols-1 md:grid-cols-4 gap-6 mb-8")}>
          <div className="bg-background rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-4">
              <FolderOpen className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-semibold">Total Projects</h3>
            </div>
            <p className="text-3xl font-bold">{stats.totalProjects}</p>
          </div>

          <div className="bg-background rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-4">
              <TrendingUp className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-semibold">Recent Projects</h3>
            </div>
            <p className="text-3xl font-bold">{stats.recentProjects}</p>
          </div>

          <div className="bg-background rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-4">
              <Share2 className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-semibold">Published</h3>
            </div>
            <p className="text-3xl font-bold">{stats.publishedProjects}</p>
          </div>

          {stats.failedProjects > 0 ? (
            <div className="bg-red-50 rounded-xl p-6 shadow-sm border border-red-200">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-800">Failed</h3>
              </div>
              <p className="text-3xl font-bold text-red-600">{stats.failedProjects}</p>
            </div>
          ) : (
            <div className="bg-background rounded-xl p-6 shadow-sm border border-border">
              <div className="flex items-center mb-4">
                <Video className="h-5 w-5 text-primary mr-2" />
                <h3 className="font-semibold">Total Videos</h3>
              </div>
              <p className="text-3xl font-bold">{stats.totalVideos}</p>
            </div>
          )}
        </div>

        {/* Recent Projects Section */}
        <div className={cn(slideUp(300), "bg-background rounded-xl shadow-sm border border-border p-6")}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <FolderOpen className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-xl font-semibold">Recent Projects</h2>
            </div>
            <Button onClick={handleCreateProject}>
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.slice(0, 6).map(project => (
                <div
                  key={project.id}
                  className="border border-border/50 rounded-lg p-4 hover:bg-accent/5 transition-colors cursor-pointer"
                  onClick={() => handleViewProject(project.id)}
                >
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium mb-1 truncate">{project.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {project.description || 'No description'}
                      </p>
                    </div>
                    <span className={cn(
                      'px-2 py-1 text-xs font-medium rounded-full ml-2 whitespace-nowrap',
                      getStatusColor(project.status)
                    )}>
                      {getStatusDisplayName(project.status)}
                    </span>
                  </div>

                  {/* Error Message for Failed Projects */}
                  {project.status === 'Failed' && project.processing_warnings && project.processing_warnings.length > 0 && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="text-red-700">
                          {typeof project.processing_warnings[0] === 'string' 
                            ? project.processing_warnings[0] 
                            : project.processing_warnings[0]?.message || 'Processing failed'
                          }
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Project Stats */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                    <div className="flex items-center">
                      <Video className="h-4 w-4 mr-1" />
                      <span>{project.compilation_metadata?.videoCount || 0} videos</span>
                    </div>
                    <div className="flex items-center">
                      <span>{project.content_types?.name || 'Unknown'}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project.id);
                      }}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    {project.status === 'Processing Complete' || project.status === 'Uploaded to Socials' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Implement preview functionality
                        }}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Preview
                      </Button>
                    ) : project.status === 'Failed' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRetryProject(project);
                        }}
                      >
                        <Wand2 className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}/upload`);
                        }}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Add Videos
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Date */}
                  <div className="mt-3 pt-3 border-t border-border/30">
                    <p className="text-xs text-muted-foreground">
                      Updated {new Date(project.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyStateMessage
              message="No projects yet"
              description="Create your first project to start building multi-video content"
              actionLabel="Create Project"
              onAction={handleCreateProject}
            />
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteProjectDialog
          isOpen={deleteDialog.isOpen}
          onClose={handleDeleteDialogClose}
          onConfirm={handleDeleteConfirm}
          projectName={deleteDialog.project?.name || ''}
          isDeleting={deleteDialog.isDeleting}
        />

        {/* Platform Features */}
        {projects.length > 0 && (
          <div className={cn(fadeIn(400), "mt-8")}>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight mb-2">Platform Features</h2>
              <p className="text-muted-foreground">Powerful tools to create, publish, and analyze your content</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* AI Processing */}
              <div className="group relative overflow-hidden bg-background rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-8">
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                    <Wand2 className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                    AI Processing
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Let AI compile your multi-video projects into engaging content
                  </p>
                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md transition-all duration-300"
                    size="default"
                  >
                    Learn More
                  </Button>
                </div>
              </div>

              {/* Social Publishing */}
              <div className="group relative overflow-hidden bg-background rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-8">
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 mb-6 group-hover:bg-accent/20 transition-colors duration-300">
                    <Share2 className="h-7 w-7 text-accent-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                    Social Publishing
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Publish directly to YouTube, TikTok, Instagram, and Facebook
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full border-accent/20 text-accent-foreground hover:bg-accent/5 hover:border-accent/30 shadow-sm hover:shadow-md transition-all duration-300"
                    size="default"
                  >
                    Connect Accounts
                  </Button>
                </div>
              </div>

              {/* Analytics */}
              <div className="group relative overflow-hidden bg-background rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-8">
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary/80 mb-6 group-hover:bg-secondary transition-colors duration-300">
                    <TrendingUp className="h-7 w-7 text-secondary-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                    Analytics
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Track performance across all your published content
                  </p>
                  <Button 
                    variant="outline"
                    className="w-full border-secondary/30 text-secondary-foreground hover:bg-secondary/10 hover:border-secondary/50 shadow-sm hover:shadow-md transition-all duration-300"
                    size="default"
                    onClick={() => navigate('/analytics')}
                  >
                    View Reports
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDashboard; 